import { cn } from "cn";
import { type VariantProps, cva } from "class-variance-authority";

export const Card = ({
  children,
  title,
  className,
  insetChild,
  header,
  overlayVisible = true,
  fillHeight = false,
  titleIcon,
  titleClassName,
}: {
  children: React.ReactNode;
  title?: string | React.ReactNode;
  className?: string;
  insetChild?: boolean;
  header?: React.ReactNode;
  overlayVisible?: boolean;
  fillHeight?: boolean;
  titleIcon?: React.ReactNode;
  titleClassName?: string;
}) => {
  return (
    <div
      className={cn("w-full transition-colors h-fit", className, {
        "p-4 bg-gray-100/50 dark:bg-gray-900/50": overlayVisible,
        "h-full *:!h-full": fillHeight,
      })}
      style={{
        borderRadius: 12,
      }}
    >
      <div
        className={cn(
          "flex flex-col bg-white dark:bg-slate-950 overflow-hidden",
          {
            "shadow-[0_0_0_1px_rgba(0,0,0,0.08)]": overlayVisible,
            "shadow-sm": !overlayVisible,
          },
        )}
        style={{
          borderRadius: 8,
        }}
      >
        {(title || header) && (
          <div
            className={cn(
              "py-3 px-4 border-b border-neutral-100 dark:border-neutral-800",
            )}
          >
            <div className="relative flex justify-between items-center">
              {titleIcon ? (
                <div className="flex items-center gap-2">
                  <div className="flex-shrink-0">{titleIcon}</div>
                  {title && (
                    <h1
                      className={cn(
                        "text-sm text-neutral-700 dark:text-neutral-300 font-medium",
                        titleClassName,
                      )}
                    >
                      {title}
                    </h1>
                  )}
                </div>
              ) : (
                <>
                  {title && (
                    <h1
                      className={cn(
                        "text-sm text-neutral-700 dark:text-neutral-300 font-medium",
                        titleClassName,
                      )}
                    >
                      {title}
                    </h1>
                  )}
                </>
              )}
              {header}
            </div>
          </div>
        )}
        <div
          className={cn(
            "py-3 px-4 text-neutral-600 dark:text-neutral-400 text-sm",
            {
              "p-0": insetChild,
              "h-full flex flex-col *:data-footer:!mt-auto": fillHeight,
            },
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export const CardFooter = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={cn(
        "bg-gray-50 dark:bg-gray-900 rounded-b-lg border-t py-2 px-4 w-[calc(100%+32px)] -mb-3! flex [&:has(>:only-child)]:justify-end [&:has(>:not(:only-child))]:justify-between -mx-4 mt-4 z-10 relative",
        className,
      )}
      data-footer
    >
      {children}
    </div>
  );
};

export const cardInsetVariants = cva("-mx-4 pt-2.5 pb-3 px-4 -mb-4 border-t", {
  variants: {
    variant: {
      default: "bg-neutral-50 dark:bg-neutral-900",
      destructive:
        "bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-950 dark:text-rose-200 dark:border-rose-800",
      warning:
        "bg-orange-50 text-orange-950 border-orange-200 dark:bg-orange-950 dark:text-orange-100 dark:border-orange-800",
      info: "bg-primary-50 text-primary-950 border-primary-200 dark:bg-primary-950 dark:text-primary-100 dark:border-primary-800",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

export const cardInsetTint = {
  default: undefined,
  destructive: "destructive",
  warning: "warning",
  info: undefined,
} as const;

export type CardInsetProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof cardInsetVariants> & {
    children: React.ReactNode;
  };

export const CardInset = ({
  children,
  className,
  variant,
  ...props
}: CardInsetProps) => {
  return (
    <div
      data-tint={cardInsetTint[variant ?? "default"]}
      className={cardInsetVariants({ variant, className })}
      {...props}
    >
      {children}
    </div>
  );
};
